import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  FilterType,
  TableColumn,
  TablesComponent
} from '@shared/components/tables/tables.component';
import { Observable, of } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { map } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { UserService } from '@core/services/user.service';
import { DEFAULT_PAGE_SIZE } from '@shared/variables/constant-variable';
import {
  fullNamePropertyCreator,
  truncateArray
} from '@shared/utils/object-mapper.util';
import {
  sortArrayAlphabetically,
  sortArrayAlphabeticallyDevelopers
} from '@shared/utils/sort.util';
import { PropertiesService } from '@core/services/properties.service';

const developerUrl = `${environment.apiBaseUrl}platform/developers`;
@Injectable({
  providedIn: SharedModule
})
export class TeamsHealthProfilesTableService {

  constructor(private http: HttpClient) {
  }

  list(params: any): Observable<Developer.DeveloperModel[]> {
    return this.http.get<Developer.DeveloperModel[]>(`${developerUrl}`);
  }

  delete(id: string): Observable<any> {
    return this.http.delete<any>(`${developerUrl}/${id}`);
  }
}
@UntilDestroy()
@Component({
  selector: 'app-teams-health-profiles-table',
  templateUrl: './teams-health-profiles-table.component.html',
  styleUrls: ['./teams-health-profiles-table.component.scss']
})

export class TeamsHealthProfilesTableComponent implements OnChanges,OnInit,AfterContentChecked  {
  @ViewChild(TablesComponent) tablesComponent!: TablesComponent;
  @Output() clickUpdate = new EventEmitter<void>();
  @Output() developersListUpdated = new EventEmitter<any[]>();
  @Output() deleteFunction = new EventEmitter<any[]>();
  @Input() searchValue?: string;
  @Input() activeProfile?: any;
  @Input() individualScorecard?: any;
  @Input() editableDevelopers?: any;
  @Input() showManageProfileColumns: boolean;
  @Input() initialDevelopers: any;
  @Input() selectedIndex: any;
  loading: boolean;
  activeUser: ActiveUser.ActiveUserModel;
  teamsList: any[] = [];
  initialLoading= true;
  activeProfileId= null;
  tooltipContent: any;
  developersList: any;

  columns: TableColumn[] = [
    {
      key: 'profilesColumnName',
      title:'Profiles',
      align: 'left',
      resizable: true,
      comp: 'cellTemplate',
      width: '300px',
      alwaysVisible: true,
      sortFn: (a: any, b: any) => {
        const valueA = a.fullName;
        const valueB = b.fullName;
        if(a.id === this.activeUser.developerId) {
          return -1
        }
        if(b.id === this.activeUser.developerId) {
          return 1
        }
        return valueA.localeCompare(valueB);

      },
    },
    {
      title: 'Status',
      key: 'enabled',
      filterType: FilterType.DROPDOWN,
      filterOptions: [
        { text: 'Active', value: true, selected: false },
        { text: 'Inactive', value: false, selected: false }
      ],
      filterEnabled: true,
      filterFn: () =>{
        return true
      },
      resizable: true,
      align: 'left',
      width: '180px',
      render: (user: User.UserModel) => {
        return {
          text: user.enabled ? 'Active' : 'Inactive',
          styles: { color: user.enabled ? '#373737' : '#898989' }
        };
      },
      comp: 'tags'
    },

    {
      key: 'companyRole',
      title:'Company Role',
      comp: 'cellTemplate',
      resizable: true,
      align: 'left',
      width: '180px',
    },

    {
      key: 'teams',
      title:'Teams',
      align: 'left',
      comp: 'cellTemplate',
      resizable: true,
      filterType: FilterType.DROPDOWN,
      filterOptions: this.teamsList,
      filterEnabled: true,
      width: '300px',
      filterFn: () =>{
        return true
      }
    },

    {
      title:'Manage Profile',
      key: 'manage',
      width: '150px',
      resizable: true,
      comp: 'cellTemplate',
      align: 'center',
    },
  ];

  constructor(
    private ps: TeamsHealthProfilesTableService,
    private userService: UserService,
    public propertiesService: PropertiesService,
    private cdref: ChangeDetectorRef
  ) { }

  static mapper(item: any): any {

    if (item.teams) {
      item = fullNamePropertyCreator(item);
      item.displayTeams = truncateArray(item.teams, 'name');
    }

    if (item.addonAccounts) {
      item.addonAccountsTooltip = item.addonAccounts.join('<br>');
    }

    return item;
  }

  ngOnInit(): void {
    if(this.selectedIndex === 0) {
      return
    }
    this.activeUser = this.userService.activeUser;
    this.activeProfileId = this.activeProfile?.id || null;

    this.loadProfiles({
      "pageIndex": 1,
      "pageSize": 40,
      "sort": [],
      "filter": null,
      "selectedFilters": null
    }).subscribe((data:any) => {
      const existId = [];

      data.data.forEach((row) => {
        row.teams.forEach(team => {
          if(existId.includes(team.id)) {
            return true
          }
          this.teamsList.push({
            value: team.id,
            text: team.name
          });
          existId.push(team.id);

        })

      })
      this.initialLoading = false;
    })
  }

  ngOnChanges(): void {
    if (this.tablesComponent) {
      this.tablesComponent.loadData({pageIndex:1});
    }
  }

  loadProfiles = (params) => {
    const hasFilter = params.filter && (params.filter.teams?.length > 0 || params.filter.enabled?.length > 0);
    const hasSearch = this.searchValue && this.searchValue.trim() !== '';
    const useCache = this.initialDevelopers?.length > 0 && !hasFilter && !hasSearch && params.pageIndex === 1;

    if (useCache) {
      let res = [...this.initialDevelopers];

      res = res.filter(data => {
        const teamIds = data.teams?.map(team => team.id);
        const selectedTeamIds = params.filter?.teams || [];
        return selectedTeamIds.length < 1 || teamIds.some(item => selectedTeamIds.includes(item));
      });

      res = res.filter(data => {
        const fullName = (data.fullName || '').toLowerCase();
        const name = (data.name || '').toLowerCase();
        const searchValue = (this.searchValue || '').toLowerCase();

        const isSearchFullName = fullName.indexOf(searchValue) > -1;
        const isSearchName = name.indexOf(searchValue) > -1;

        return (isSearchFullName || isSearchName) || (searchValue === '');
      });

      res = res.filter(data => {
        const selectedEnable = params.filter?.enabled || [];
        return selectedEnable.includes(data.enabled) || selectedEnable.length === 0;
      });
      res = sortArrayAlphabeticallyDevelopers(res.map(TeamsHealthProfilesTableComponent.mapper), 'fullName');
      res = sortArrayAlphabetically(res?.map(fullNamePropertyCreator), 'fullName')
        .sort((a, b) => (a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1);
      if(this.activeProfileId!=null  ) {
        res.unshift(this.activeProfile);
      }


      res = res.filter((item, index) => {
        if (index === 0) { return true; }
        return item.id !== this.activeProfileId;
      });


      const totalSize = res.length;
      this.developersList = res.filter(item => item.id !== this.activeProfileId);
      this.developersListUpdated.emit(this.developersList);

      const startIndex = (params.pageIndex - 1) * DEFAULT_PAGE_SIZE;
      const paginatedRes = res.slice(startIndex, startIndex + DEFAULT_PAGE_SIZE);

      return of({
        data: paginatedRes,
        pagination: {
          total: totalSize,
          size: DEFAULT_PAGE_SIZE
        }
      });

    }
    return this.ps.list(params).pipe(map((res: any) => {
      res = res.filter(data => {
        const teamIds = data.teams?.map(team => team.id);
        const selectedTeamIds = params.filter?.teams || [];
        return selectedTeamIds.length < 1 || teamIds.some(item => selectedTeamIds.includes(item));
      });

      res = res.filter(data => {
        const fullName = (data.fullName || '').toLowerCase();
        const name = (data.name || '').toLowerCase();
        const searchValue = (this.searchValue || '').toLowerCase();

        const isSearchFullName = fullName.indexOf(searchValue) > -1;
        const isSearchName = name.indexOf(searchValue) > -1;

        return (isSearchFullName || isSearchName) || (searchValue === '');
      });

      res = res.filter(data => {
        const selectedEnable = params.filter?.enabled || [];
        return selectedEnable.includes(data.enabled) || selectedEnable.length === 0;
      });


      res = sortArrayAlphabeticallyDevelopers(res.map(TeamsHealthProfilesTableComponent.mapper), 'fullName');
      res = sortArrayAlphabetically(res?.map(fullNamePropertyCreator), 'fullName')
        .sort((a, b) => (a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1);

      if(this.activeProfileId!=null  ) {
        res.unshift(this.activeProfile);
      }

      res = res.filter((item, index) => {
        if (index === 0) { return true; }
        return item.id !== this.activeProfileId;
      });

      const totalSize = res.length;

      const filteredList = res.filter(item => item.id !== this.activeProfileId);

      this.developersList = filteredList;
      this.developersListUpdated.emit(this.developersList);

      const startIndex = (params.pageIndex - 1) * DEFAULT_PAGE_SIZE;
      const paginatedRes = res.slice(startIndex, startIndex + DEFAULT_PAGE_SIZE);
      return {
        data: paginatedRes,
        pagination: {
          total: totalSize,
          size: DEFAULT_PAGE_SIZE
        }
      };
    }));

  }


  getRemainingRolesTooltip(teams: string[]): string {
    return teams.slice(2).map((item: any) => item.name).join(', ');
  }

  delete(id): void {
    this.deleteFunction.emit(id)
  }

  update(data):void {
    this.clickUpdate.emit(data);
  }

  formatSnakeCase(text: string): string {
    if (!text) { return ''; }
    return text
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }
}

